import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from '../../environments/environment';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { LoginService } from '../services/login.service';
import { ToastrService } from 'ngx-toastr';
import { SellersService } from '../services/sellers.service';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, CommonModule, NgxSpinnerComponent, RouterModule, TranslocoModule, LanguageSwitcherComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  animations: [
    // Anima a altura do conteúdo ao alternar entre as formas de login.
    trigger('tabResize', [
      transition('ml <=> password', [
        style({ height: '{{ startHeight }}px', overflow: 'hidden' }),
        animate('0.35s cubic-bezier(0.22, 1, 0.36, 1)', style({ height: '*' })),
      ], { params: { startHeight: 0 } }),
    ]),
  ],
})
export class LoginComponent implements OnInit {

  activeTab: string = 'ml';
  email: string = '';
  password: string = '';
  selectedSeller: any;
  sellers: any = [];
  previousTabHeight = 0;

  @ViewChild('tabContent') tabContent?: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router,
    private loginService: LoginService,
    private toastr: ToastrService,
    private sellersService: SellersService,
    private spinner: NgxSpinnerService,
    private transloco: TranslocoService
  ) { }


  setTab(tab: string) {
    if (tab === this.activeTab) {
      return;
    }
    // Captura a altura atual antes da troca para animar até a nova altura.
    this.previousTabHeight = this.tabContent?.nativeElement.offsetHeight ?? 0;
    this.activeTab = tab;
  }

  loginMercadoLivre() {
    const clientId = environment.client_id;
    const redirectUri = environment.redirect_uri;
    const state = '12346';

    const authUrl = `https://auth.mercadolivre.com.br/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}`;

    window.location.href = authUrl; // Redireciona o usuário
  }

  loginWithPassword(): void {
    if (this.sellers.length === 0) {
      // Mostrar loading ao fazer login inicial
      this.spinner.show(undefined, {
        bdColor: 'rgba(0, 0, 0, 0.7)',
        size: 'medium',
        color: '#ffffff',
        type: 'ball-grid-pulse',
        fullScreen: true
      });

      this.loginService.loginWithPassword(this.email, this.password)
        .subscribe({
          next: response => {
            localStorage.setItem('jwt-password', response.access_token);
            
            // Buscar sellers com loading
            this.sellersService.getAll()
              .subscribe({
                next: sellers => {
                  this.sellers = sellers.data;
                  this.spinner.hide();
                },
                error: error => {
                  this.spinner.hide();
                  this.toastr.error(this.transloco.translate('toasts.loadSellersError'));
                }
              });
          },
          error: error => {
            this.spinner.hide();
            this.toastr.error(this.transloco.translate('toasts.invalidCredentials'));
          }
        });
    }
    else {
      if (this.selectedSeller) {
        this.loginWithCode("", this.selectedSeller.sellerId);
      } else {
        this.toastr.error(this.transloco.translate('toasts.selectSeller'));
      }
    }

  }


  ngOnInit(): void {
    const token = localStorage.getItem('jwt');

    // Verifica se o token já existe, redireciona para /home
    if (token) {
      this.router.navigate(['/home']);
    } else {
      this.route.queryParams.subscribe(params => {
        const code = params['code'];
        const state = params['state'];
        const tab = params['tab'];

        // Verificar se deve abrir uma aba específica (ex: quando vem do forgot-password)
        if (tab === 'password') {
          this.activeTab = 'password';
        }

        if (code) {
          // Mostrar loading imediatamente quando detectar o código do callback do Mercado Livre
          this.spinner.show(undefined, {
            bdColor: 'rgba(0, 0, 0, 0.7)',
            size: 'medium',
            color: '#ffffff',
            type: 'ball-grid-pulse',
            fullScreen: true
          });
          
          // Pequeno delay para garantir que o DOM esteja pronto antes de processar o login
          setTimeout(() => {
            this.loginWithCode(code);
          }, 100);
        }
      });
    }
  }

  loginWithCode(code: string, sellerId?: number) {
    // Mostrar loading ao fazer login com código (pode já estar visível se chamado do ngOnInit)
    this.spinner.show(undefined, {
      bdColor: 'rgba(0, 0, 0, 0.7)',
      size: 'medium',
      color: '#ffffff',
      type: 'ball-grid-pulse',
      fullScreen: true
    });

    let query = `?code=${code}`;
    if(sellerId)
       query += `&sellerId=${sellerId}`;

    // Chame o endpoint de login para obter o access token
    this.http.get<any>(`${environment.apiUrl}/login${query}`).subscribe({
      next: response => {
        const token = response.access_token;

        // Armazene o JWT no localStorage
        localStorage.setItem('jwt', token);
        localStorage.removeItem('jwt-password');
        localStorage.setItem("username", response.name);

        // Esconder loading antes do reload
        this.spinner.hide();

        // Redireciona para a tela inicial
        location.reload();
        this.router.navigate(['/home']);
      },
      error: error => {
        this.spinner.hide();
        console.error('Erro ao fazer login:', error);
        this.toastr.error(this.transloco.translate('toasts.loginError'));
      }
    });
  }
}
